import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSyncAlt, faDownload, faExpand, faCompress, 
  faColumns, faWindowRestore, faRedo, faKeyboard, faChartBar 
} from '@fortawesome/free-solid-svg-icons';
import { getSelectedVehicle } from '../../utils/folderUtils';
import { getDateRangeFromLocalStorage } from '../../utils/DateUtils';
import SplitScreenContainer from '../common/SplitScreenContainer';
import splitScreenManager, { SPLIT_MODES } from '../../utils/SplitScreenManager';
import ReportChooser from './ReportChooser';
import { toast } from 'react-toastify';
import './StatisticsReport.css';

const StatisticsReport = () => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [expandedMode, setExpandedMode] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [showReportChooser, setShowReportChooser] = useState(false);
  const [collapsedSummary, setCollapsedSummary] = useState(false);
  const [collapsedDetails, setCollapsedDetails] = useState(false);
  const [collapsedAll, setCollapsedAll] = useState(false);
  const [containerToFill, setContainerToFill] = useState(null);
  const containerId = `statistics-container-${Math.random().toString(36).substring(2, 11)}`;
  
  // Добавляем ref для контейнера статистики
  const statisticsContainerRef = useRef(null);

  // Загрузка данных из localStorage
  useEffect(() => {
    const loadStoredData = () => {
      // Получаем выбранное транспортное средство
      const storedVehicle = getSelectedVehicle();
      if (storedVehicle) {
        setVehicle(storedVehicle);
      }

      // Получаем диапазон дат
      const storedDateRange = getDateRangeFromLocalStorage();
      if (storedDateRange.startDate && storedDateRange.endDate) {
        setDateRange(storedDateRange);
      }
    };

    loadStoredData();

    // Слушаем изменения в localStorage
    const handleStorageChange = () => {
      loadStoredData();
    };

    window.addEventListener('storage', handleStorageChange);
    document.addEventListener('vehicleChanged', loadStoredData);
    document.addEventListener('dateRangeChanged', loadStoredData);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('vehicleChanged', loadStoredData);
      document.removeEventListener('dateRangeChanged', loadStoredData);
    };
  }, []);

  // Функция для получения токена авторизации
  const getAuthToken = useCallback(() => {
    // Проверяем различные возможные ключи для токена
    const possibleTokenKeys = ['authToken', 'token', 'jwt', 'access_token', 'sessionToken'];
    
    for (const key of possibleTokenKeys) {
      const token = localStorage.getItem(key);
      if (token && token.trim() !== '') {
        console.log(`Найден токен в localStorage под ключом: ${key}`);
        return token;
      }
    }
    
    // Проверяем sessionStorage
    for (const key of possibleTokenKeys) {
      const token = sessionStorage.getItem(key);
      if (token && token.trim() !== '') {
        console.log(`Найден токен в sessionStorage под ключом: ${key}`);
        return token;
      }
    }
    
    console.warn('Токен авторизации не найден в localStorage или sessionStorage');
    return null;
  }, []);

  // Функция для загрузки статистики
  const fetchStatistics = useCallback(async () => {
    const requestId = `${vehicle?.imei}-${Date.now()}`;
    console.log('fetchStatistics вызвана для:', {
      requestId,
      vehicle: vehicle?.name,
      imei: vehicle?.imei,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      timestamp: new Date().toISOString()
    });

    if (!vehicle || !vehicle.imei || !dateRange.startDate || !dateRange.endDate) {
      console.log('Недостаточно данных для загрузки статистики:', {
        requestId,
        vehicle: vehicle?.name,
        imei: vehicle?.imei,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      return;
    }

    setLoading(true);
    setError(null);
    setStatistics(null); // Сбрасываем предыдущие данные

    try {
      const token = getAuthToken();
      
      if (!token) {
        throw new Error('Токен авторизации не найден. Пожалуйста, войдите в систему заново.');
      }
      
      // Форматируем даты для API
      const startTime = new Date(dateRange.startDate).toISOString();
      const endTime = new Date(dateRange.endDate).toISOString();

      const apiUrl = `https://telemetry.omnicomm-expert.kz/api/statistic/${vehicle.imei}?startTime=${startTime}&endTime=${endTime}`;
      
      console.log('Запрос статистики:', {
        requestId,
        url: apiUrl,
        vehicle: vehicle.name,
        imei: vehicle.imei,
        startTime,
        endTime,
        hasToken: !!token,
        timestamp: new Date().toISOString()
      });

      // Подготавливаем заголовки запроса
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Доступ запрещен. Проверьте права доступа или войдите в систему заново.');
        } else if (response.status === 401) {
          throw new Error('Не авторизован. Пожалуйста, войдите в систему.');
        } else if (response.status === 404) {
          // Если данных нет, устанавливаем пустую статистику
          setStatistics({
            status: 'success',
            data: [],
            summary: null,
            totalRecords: 0,
            returnedRecords: 0,
            message: 'Нет данных за выбранный период'
          });
          console.log('Данные не найдены для указанного периода');
          return;
        } else {
          throw new Error(`Ошибка HTTP: ${response.status} ${response.statusText}`);
        }
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        // Проверяем, есть ли данные
        if (!data.data || data.data.length === 0) {
          setStatistics({
            status: 'success',
            data: [],
            summary: null,
            totalRecords: 0,
            returnedRecords: 0,
            message: 'Нет данных за выбранный период'
          });
          console.log('Получен пустой ответ от API для:', requestId);
        } else {
          setStatistics(data);
          console.log('Статистика успешно загружена для:', requestId, data);
        }
      } else {
        // Если статус не success, но есть данные
        if (data.message && data.message.includes('не найден') || data.message && data.message.includes('нет данных')) {
          setStatistics({
            status: 'success',
            data: [],
            summary: null,
            totalRecords: 0,
            returnedRecords: 0,
            message: data.message || 'Нет данных за выбранный период'
          });
        } else {
          throw new Error(data.message || 'Неизвестная ошибка при получении статистики');
        }
      }

    } catch (err) {
      console.error('Ошибка при загрузке статистики для:', requestId, err);
      setError(err.message);
      setStatistics(null);
    } finally {
      setLoading(false);
    }
  }, [vehicle, dateRange, getAuthToken]);

  // Автоматическая загрузка статистики при изменении данных
  useEffect(() => {
    if (vehicle && vehicle.imei && dateRange.startDate && dateRange.endDate) {
      fetchStatistics();
    }
  }, [fetchStatistics]);

  // Сброс данных при смене транспортного средства или диапазона дат
  useEffect(() => {
    // Сбрасываем статистику при смене транспортного средства или дат
    setStatistics(null);
    setError(null);
    console.log('Сброс данных статистики при смене параметров:', {
      vehicle: vehicle?.name,
      imei: vehicle?.imei,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate
    });
  }, [vehicle?.imei, dateRange.startDate, dateRange.endDate]);

  // Обработка событий разделения экрана
  useEffect(() => {
    // Создаем переменную для отслеживания, было ли уже вызвано модальное окно
    let modalTriggered = false;

    const handleSplitComplete = (event) => {
      const { containerId: eventContainerId, container1Id, container2Id, direction } = event.detail;
      
      // Проверяем, что это событие для нашего контейнера и что модальное окно еще не было вызвано
      if (eventContainerId === containerId && !modalTriggered) {
        console.log(`StatisticsReport: Получено событие завершения разделения для нашего контейнера ${containerId}`);
        console.log(`StatisticsReport: Новые контейнеры: ${container1Id}, ${container2Id}, направление: ${direction}`);
        
        // Устанавливаем флаг, что модальное окно вызвано
        modalTriggered = true;
        
        // Открываем окно выбора отчета для второго контейнера
        setContainerToFill(container2Id);
        setShowReportChooser(true);
        
        // Устанавливаем флаг глобально, что модальное окно открыто
        window.reportChooserModalOpen = true;
        
        // Сбрасываем флаг при закрытии модального окна через некоторое время
        setTimeout(() => {
          modalTriggered = false;
        }, 1000);
      }
    };

    const handleRequestReportSelector = (event) => {
      const { containerId: targetContainerId, activateContainer } = event.detail;
      
      // Проверяем глобальный флаг, что модальное окно еще не открыто где-то
      if (window.reportChooserModalOpen) {
        console.log(`StatisticsReport: Пропускаем запрос выбора отчета, модальное окно уже открыто в другом экземпляре`);
        return;
      }
      
      // Проверяем локальное состояние
      if (showReportChooser || modalTriggered) {
        console.log(`StatisticsReport: Пропускаем повторный запрос выбора отчета, так как модальное окно уже ${showReportChooser ? 'открыто' : 'запрошено'}`);
        return;
      }
      
      // Устанавливаем флаг, что запрос обработан
      modalTriggered = true;
      
      // Устанавливаем глобальный флаг
      window.reportChooserModalOpen = true;
      
      console.log(`StatisticsReport: Получен запрос на открытие селектора отчетов для контейнера ${targetContainerId}`);
      
      // Устанавливаем целевой контейнер и открываем выбор отчета
      setContainerToFill(targetContainerId);
      setShowReportChooser(true);
      
      // Активируем контейнер для выбора отчета, если требуется
      if (activateContainer) {
        const container = document.getElementById(targetContainerId) || 
                         document.querySelector(`[data-container-id="${targetContainerId}"]`);
        
        if (container) {
          // Сначала снимаем активность со всех контейнеров
          document.querySelectorAll('.split-screen-container[data-active="true"]')
            .forEach(el => {
              if (el && document.body.contains(el)) {
                el.setAttribute('data-active', 'false');
                el.classList.remove('active-container');
              }
            });
          
          // Активируем целевой контейнер
          container.setAttribute('data-active', 'true');
          container.classList.add('active-container');
        }
      }
    };

    const handleCreateReport = (event) => {
      const { reportType, container, vehicle, startDate, endDate } = event.detail;
      
      console.log(`StatisticsReport: Получено событие создания отчета ${reportType} для контейнера ${container}`);
      
      // Проверяем, что контейнер существует
      const targetContainer = document.getElementById(container) || 
                             document.querySelector(`[data-container-id="${container}"]`);
      
      if (!targetContainer) {
        console.warn(`StatisticsReport: Целевой контейнер ${container} не найден в DOM`);
        return;
      }
      
      // Создаем отчет для указанного контейнера через систему событий ReportsPage
      console.log(`StatisticsReport: Перенаправляем создание отчета ${reportType} в ReportsPage`);
      
      // Отправляем событие для ReportsPage
      const createReportEvent = new CustomEvent('createReportInContainer', {
        detail: {
          reportType: reportType,
          containerId: container,
          vehicle: vehicle,
          startDate: startDate,
          endDate: endDate,
          timestamp: Date.now()
        }
      });
      
      document.dispatchEvent(createReportEvent);
    };

    const handleReportSelected = (event) => {
      const { reportType, sourceContainerId } = event.detail;
      if (sourceContainerId === containerId) {
        console.log(`StatisticsReport: Обработка выбора отчета ${reportType} для контейнера ${containerId}`);
        // Здесь можно добавить дополнительную логику обработки выбранного отчета
      }
    };

    // Добавляем обработчики событий
    document.addEventListener('splitContainerComplete', handleSplitComplete);
    document.addEventListener('requestReportSelector', handleRequestReportSelector);
    document.addEventListener('createReport', handleCreateReport);
    document.addEventListener('reportSelected', handleReportSelected);

    // Очистка при размонтировании
    return () => {
      document.removeEventListener('splitContainerComplete', handleSplitComplete);
      document.removeEventListener('requestReportSelector', handleRequestReportSelector);
      document.removeEventListener('createReport', handleCreateReport);
      document.removeEventListener('reportSelected', handleReportSelected);
    };
  }, [containerId, showReportChooser]);

  // Функция для форматирования времени
  const formatTime = (minutes) => {
    if (!minutes) return '0 мин';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours} ч ${mins} мин` : `${mins} мин`;
  };

  // Функция для форматирования расстояния
  const formatDistance = (distance) => {
    if (!distance) return '0 км';
    // Всегда показываем в километрах для лучшей читаемости
    return `${(distance ).toFixed(2)} км`;
  };

  // Функция для форматирования скорости
  const formatSpeed = (speed) => {
    if (!speed) return '0 км/ч';
    return `${speed.toFixed(1)} км/ч`;
  };

  // Функции для контроллеров заголовка
  const handleRefresh = () => {
    fetchStatistics();
    toast.info('Обновление данных статистики...', { autoClose: 1500 });
  };

  const toggleExpandedMode = () => {
    setExpandedMode(!expandedMode);
    if (!expandedMode) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const exportData = () => {
    if (!statistics || !statistics.data || statistics.data.length === 0) {
      toast.error('Нет данных для экспорта');
      return;
    }

    try {
      // Создаем CSV данные
      const headers = ['Время', 'Скорость', 'Расстояние', 'Пробег', 'Общий пробег'];
      const csvContent = [
        headers.join(','),
        ...statistics.data.map(item => [
          item.time,
          item.speed || 0,
          item.distance || 0,
          item.mileage || 0,
          item.totalMileage || 0
        ].join(','))
      ].join('\n');

      // Создаем и скачиваем файл
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `statistics_${vehicle?.name || 'vehicle'}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Данные экспортированы в CSV файл');
    } catch (err) {
      console.error('Ошибка при экспорте:', err);
      toast.error('Ошибка при экспорте данных');
    }
  };

  const toggleKeyboardShortcuts = () => {
    setShowKeyboardShortcuts(!showKeyboardShortcuts);
  };

  const toggleSummaryCollapse = () => {
    setCollapsedSummary(!collapsedSummary);
  };

  const toggleDetailsCollapse = () => {
    setCollapsedDetails(!collapsedDetails);
  };

  const toggleAllCollapse = () => {
    setCollapsedAll(!collapsedAll);
    // При сворачивании всего, сворачиваем и отдельные разделы
    if (!collapsedAll) {
      setCollapsedSummary(true);
      setCollapsedDetails(true);
    } else {
      setCollapsedSummary(false);
      setCollapsedDetails(false);
    }
  };

  const handleHorizontalSplit = () => {
    // Находим контейнер перед разделением
    const container = statisticsContainerRef.current?.closest('.split-screen-container');
    if (!container) {
      console.error('StatisticsReport: Не удалось найти контейнер для разделения');
      return;
    }
    
    // Активируем контейнер
    document.querySelectorAll('.split-screen-container[data-active="true"]')
      .forEach(el => {
        el.setAttribute('data-active', 'false');
        el.classList.remove('active-container');
      });
    
    container.setAttribute('data-active', 'true');
    container.classList.add('active-container');
    
    // Получаем ID контейнера
    const targetContainerId = container.id || container.getAttribute('data-container-id');
    if (!targetContainerId) {
      console.error('StatisticsReport: У контейнера нет ID для разделения');
      return;
    }
    
    console.log(`StatisticsReport: Разделяем контейнер ${targetContainerId} горизонтально`);
    
    // Создаем пользовательское событие для запроса на разделение
    const splitEvent = new CustomEvent('requestSplit', {
      detail: {
        direction: 'horizontal',
        targetContainerId: targetContainerId,
        timestamp: Date.now(),
        processed: false
      }
    });
    
    document.dispatchEvent(splitEvent);
    
    // После отправки события закрываем выбор отчетов, если он открыт
    if (showReportChooser) {
      setShowReportChooser(false);
    }
  };

  const handleVerticalSplit = () => {
    // Находим контейнер перед разделением
    const container = statisticsContainerRef.current?.closest('.split-screen-container');
    if (!container) {
      console.error('StatisticsReport: Не удалось найти контейнер для разделения');
      return;
    }
    
    // Активируем контейнер
    document.querySelectorAll('.split-screen-container[data-active="true"]')
      .forEach(el => {
        el.setAttribute('data-active', 'false');
        el.classList.remove('active-container');
      });
    
    container.setAttribute('data-active', 'true');
    container.classList.add('active-container');
    
    // Получаем ID контейнера
    const targetContainerId = container.id || container.getAttribute('data-container-id');
    if (!targetContainerId) {
      console.error('StatisticsReport: У контейнера нет ID для разделения');
      return;
    }
    
    console.log(`StatisticsReport: Разделяем контейнер ${targetContainerId} вертикально`);
    
    // Создаем событие для запроса разделения по вертикали
    const splitEvent = new CustomEvent('requestSplit', {
      detail: {
        direction: 'vertical',
        targetContainerId: targetContainerId,
        timestamp: Date.now(),
        processed: false
      }
    });
    
    document.dispatchEvent(splitEvent);
    
    // После отправки события закрываем выбор отчетов, если он открыт
    if (showReportChooser) {
      setShowReportChooser(false);
    }
  };

  const handleReportSelect = (selectedReportType) => {
    setShowReportChooser(false);
    // Сбрасываем глобальный флаг
    window.reportChooserModalOpen = false;
    
    console.log(`StatisticsReport: Выбран отчет: ${selectedReportType}`);
    
    if (containerToFill) {
      // Если у нас есть контейнер для заполнения, создаем отчет для этого контейнера
      console.log(`StatisticsReport: Создаем отчет типа ${selectedReportType} для контейнера ${containerToFill}`);
      
      // Отправляем событие для создания отчета в контейнере
      const createEvent = new CustomEvent('createReport', {
        detail: {
          reportType: selectedReportType,
          container: containerToFill,
          vehicle: vehicle,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          timestamp: Date.now()
        }
      });
      
      document.dispatchEvent(createEvent);
      
      // Сбрасываем контейнер
      setContainerToFill(null);
      
      toast.success(`Отчет "${selectedReportType}" добавлен в контейнер`);
      return;
    }
    
    // Отправляем событие о выборе отчета для разделенного экрана (старая логика)
    const reportSelectEvent = new CustomEvent('reportSelected', {
      detail: {
        reportType: selectedReportType,
        sourceContainerId: containerId,
        targetContainerId: containerToFill,
        vehicle: vehicle,
        dateRange: dateRange,
        timestamp: Date.now()
      }
    });
    
    document.dispatchEvent(reportSelectEvent);
    
    toast.info(`Выбран отчет: ${selectedReportType}`);
  };

  const handleReportChooserClose = () => {
    setShowReportChooser(false);
    setContainerToFill(null);
    // Сбрасываем глобальный флаг
    window.reportChooserModalOpen = false;
  };

  if (!vehicle) {
    return (
      <div className="statistics-report">
        <div className="statistics-content">
          <div className="no-data">
            <p>Выберите транспортное средство для просмотра статистики</p>
          </div>
        </div>
      </div>
    );
  }

  if (!dateRange.startDate || !dateRange.endDate) {
    return (
      <div className="statistics-report">
        <div className="statistics-content">
          <div className="no-data">
            <p>Выберите диапазон дат для просмотра статистики</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SplitScreenContainer 
        id={containerId}
        data-container-id={containerId}
        className="statistics-split-container"
        showControls={true}
        allowSplit={true}
        onSplitChange={(splitInfo) => {
          console.log('StatisticsReport: Получено событие разделения экрана', splitInfo);
          // После разделения обновляем компонент
          setTimeout(() => {
            // Принудительно обновляем размеры если нужно
            window.dispatchEvent(new Event('resize'));
          }, 200);
        }}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <div className="statistics-report" ref={statisticsContainerRef}>
          {/* Заголовок с контроллерами */}
          <div className="tm-header">
            <div className="tm-title">
              <FontAwesomeIcon icon={faChartBar} style={{ marginRight: '8px' }} /> 
              <span>Отчет статистики {vehicle && vehicle.name ? `- ${vehicle.name}` : ''}</span>
            </div>
            <div className="tm-controls">
              <div className="tm-control-group">
                <button
                  className="tm-control-button"
                  onClick={toggleKeyboardShortcuts}
                  title="Показать горячие клавиши"
                >
                  <FontAwesomeIcon icon={faKeyboard} />
                </button>
                <button
                  className="tm-control-button"
                  onClick={toggleAllCollapse}
                  title={collapsedAll ? "Развернуть статистику" : "Свернуть статистику"}
                >
                  <FontAwesomeIcon icon={collapsedAll ? faExpand : faCompress} />
                </button>
                <button
                  className="tm-control-button"
                  onClick={handleRefresh}
                  title="Обновить данные"
                >
                  <FontAwesomeIcon icon={faSyncAlt} />
                </button>
              </div>
              
              {/* Группа кнопок разделения экрана */}
              <div className="tm-control-group">
                <button
                  className="tm-control-button"
                  onClick={handleHorizontalSplit}
                  title="Разделить по горизонтали"
                >
                  <FontAwesomeIcon icon={faColumns} style={{ transform: 'rotate(90deg)' }} />
                </button>
                <button
                  className="tm-control-button"
                  onClick={handleVerticalSplit}
                  title="Разделить по вертикали"
                >
                  <FontAwesomeIcon icon={faColumns} />
                </button>
                <button
                  className="tm-control-button"
                  onClick={() => splitScreenManager.changeSplitMode(SPLIT_MODES.SINGLE)}
                  title="Один экран"
                >
                  <FontAwesomeIcon icon={faWindowRestore} />
                </button>
                <button
                  className="tm-control-button"
                  onClick={() => splitScreenManager.goBack()}
                  title="Вернуться к предыдущему режиму"
                >
                  <FontAwesomeIcon icon={faRedo} style={{ transform: 'rotate(180deg)' }} />
                </button>
              </div>
              
              <div className="tm-control-group">
                <button
                  className="tm-control-button"
                  onClick={exportData}
                  title="Экспорт данных в CSV"
                >
                  <FontAwesomeIcon icon={faDownload} />
                </button>
              </div>
              <div className="tm-control-group">
                <button
                  className="tm-control-button"
                  onClick={toggleExpandedMode}
                  title={expandedMode ? "Свернуть" : "На весь экран"}
                >
                  <FontAwesomeIcon icon={expandedMode ? faCompress : faExpand} />
                </button>
              </div>
            </div>
          </div>

          {/* Подсказки по горячим клавишам */}
          {showKeyboardShortcuts && (
            <div className="keyboard-shortcuts-tooltip" 
                 style={{
                   position: 'absolute',
                   top: '50px',
                   right: '10px',
                   background: 'rgba(0, 0, 0, 0.8)',
                   color: 'white',
                   padding: '10px',
                   borderRadius: '5px',
                   zIndex: 1000,
                   fontSize: '12px',
                   boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)'
                 }}>
              <div style={{fontWeight: 'bold', marginBottom: '5px'}}>Горячие клавиши:</div>
              <div>F5 - обновить данные</div>
              <div>Ctrl+S - экспорт данных</div>
              <div>F11 - полноэкранный режим</div>
              <div style={{marginTop: '5px', textAlign: 'center'}}>
                <button onClick={toggleKeyboardShortcuts} 
                        style={{background: 'transparent', border: '1px solid white', color: 'white', padding: '2px 5px', cursor: 'pointer'}}>
                  Закрыть
                </button>
              </div>
            </div>
          )}

          <div className="statistics-content">
            {!collapsedAll && (
              <>
                {loading && (
                  <div className="loading">
                    <div className="loading-spinner"></div>
                    <p>Загрузка статистики...</p>
                  </div>
                )}

                {error && (
                  <div className="error">
                    <p>Ошибка: {error}</p>
                    <button onClick={fetchStatistics} className="retry-button">
                      Повторить попытку
                    </button>
                  </div>
                )}

                {!vehicle && (
                  <div className="no-data">
                    <p>Выберите транспортное средство для просмотра статистики</p>
                  </div>
                )}

                {vehicle && (!dateRange.startDate || !dateRange.endDate) && (
                  <div className="no-data">
                    <p>Выберите диапазон дат для просмотра статистики</p>
                  </div>
                )}

                {statistics && statistics.summary && (
                  <div className="statistics-summary">
                    <div className="sections-header" onClick={toggleSummaryCollapse}>
                      <h3>Сводная информация</h3>
                      <button className="collapse-button" title={collapsedSummary ? "Развернуть" : "Свернуть"}>
                        <FontAwesomeIcon icon={collapsedSummary ? faExpand : faCompress} />
                      </button>
                    </div>
                    {!collapsedSummary && (
                      <div className="summary-grid">
                        <div className="summary-item">
                          <div className="summary-label">Общий пробег</div>
                          <div className="summary-value">{formatDistance(statistics.summary.totalMileage)}</div>
                        </div>
                        
                        <div className="summary-item">
                          <div className="summary-label">Пройденное расстояние</div>
                          <div className="summary-value">{formatDistance(statistics.summary.totalDistance)}</div>
                        </div>
                        
                        <div className="summary-item">
                          <div className="summary-label">Средняя скорость</div>
                          <div className="summary-value">{formatSpeed(statistics.summary.averageSpeed)}</div>
                        </div>
                        
                        <div className="summary-item">
                          <div className="summary-label">Максимальная скорость</div>
                          <div className="summary-value">{formatSpeed(statistics.summary.maxSpeed)}</div>
                        </div>
                        
                        <div className="summary-item">
                          <div className="summary-label">Время в движении</div>
                          <div className="summary-value">{formatTime(statistics.summary.totalDrivingTimeMinutes)}</div>
                        </div>
                        
                        <div className="summary-item">
                          <div className="summary-label">Время простоя</div>
                          <div className="summary-value">{formatTime(statistics.summary.totalIdleTimeMinutes)}</div>
                        </div>
                        
                        <div className="summary-item">
                          <div className="summary-label">Всего точек</div>
                          <div className="summary-value">{statistics.summary.totalPoints}</div>
                        </div>
                        
                        <div className="summary-item">
                          <div className="summary-label">Валидных точек</div>
                          <div className="summary-value">{statistics.summary.validPoints}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Блок информации о топливе */}
                {statistics && statistics.summary && (
                  <div className="fuel-info-block">
                    <div className="info-block-header">
                      <h3>🛢️ Информация о топливе</h3>
                    </div>
                    <div className="info-block-content">
                      <div className="info-grid">
                        <div className="info-item">
                          <div className="info-label">Общий расход</div>
                          <div className="info-value">
                            {statistics.summary.fuelConsumption ? 
                              `${statistics.summary.fuelConsumption.toFixed(2)} л` : 
                              'Нет данных'
                            }
                          </div>
                        </div>
                        
                        <div className="info-item">
                          <div className="info-label">Фактический расход</div>
                          <div className="info-value">
                            {statistics.summary.actualFuelConsumption ? 
                              `${statistics.summary.actualFuelConsumption.toFixed(2)} л` : 
                              'Нет данных'
                            }
                          </div>
                        </div>
                        
                        <div className="info-item">
                          <div className="info-label">Расход на 100км</div>
                          <div className="info-value">
                            {statistics.summary.fuelConsumptionPer100Km ? 
                              `${statistics.summary.fuelConsumptionPer100Km.toFixed(2)} л/100км` : 
                              'Нет данных'
                            }
                          </div>
                        </div>
                        
                        <div className="info-item">
                          <div className="info-label">Расход в движении</div>
                          <div className="info-value">
                            {statistics.summary.fuelConsumptionInMovement ? 
                              `${statistics.summary.fuelConsumptionInMovement.toFixed(2)} л` : 
                              'Нет данных'
                            }
                          </div>
                        </div>
                        
                        <div className="info-item">
                          <div className="info-label">Расход на холостом ходу</div>
                          <div className="info-value">
                            {statistics.summary.fuelConsumptionNoMovement ? 
                              `${statistics.summary.fuelConsumptionNoMovement.toFixed(2)} л` : 
                              'Нет данных'
                            }
                          </div>
                        </div>
                        
                        <div className="info-item">
                          <div className="info-label">Расход на моточас</div>
                          <div className="info-value">
                            {statistics.summary.fuelConsumptionPerEngineHour ? 
                              `${statistics.summary.fuelConsumptionPerEngineHour.toFixed(2)} л/ч` : 
                              'Нет данных'
                            }
                          </div>
                        </div>
                        
                        <div className="info-item">
                          <div className="info-label">Объем заправки</div>
                          <div className="info-value">
                            {statistics.summary.refuelVolume ? 
                              `${statistics.summary.refuelVolume.toFixed(2)} л` : 
                              'Нет данных'
                            }
                          </div>
                        </div>
                        
                        <div className="info-item">
                          <div className="info-label">Выбросы CO2</div>
                          <div className="info-value">
                            {statistics.summary.co2EmissionVolume ? 
                              `${statistics.summary.co2EmissionVolume.toFixed(2)} кг` : 
                              'Нет данных'
                            }
                          </div>
                        </div>
                        
                        {statistics.summary.idleOverconsumptionStatus && (
                          <div className="info-item status-warning">
                            <div className="info-label">Статус превышения</div>
                            <div className="info-value status-text">
                              {statistics.summary.idleOverconsumptionStatus}
                            </div>
                          </div>
                        )}
                        
                        {/* Добавляем информацию о текущем уровне топлива из последней точки */}
                        {statistics.data && statistics.data.length > 0 && statistics.data[statistics.data.length - 1].fuelLevel && (
                          <div className="info-item">
                            <div className="info-label">Текущий уровень</div>
                            <div className="info-value">
                              {statistics.data[statistics.data.length - 1].fuelLevel.toFixed(1)} л
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Блок информации о двигателе и напряжении */}
                {statistics && statistics.summary && (
                  <div className="voltage-info-block">
                    <div className="info-block-header">
                      <h3>⚡ Информация о двигателе и напряжении</h3>
                    </div>
                    <div className="info-block-content">
                      <div className="info-grid">
                        <div className="info-item">
                          <div className="info-label">Моточасы</div>
                          <div className="info-value">
                            {statistics.summary.engineHours ? 
                              `${statistics.summary.engineHours.toFixed(2)} ч` : 
                              'Нет данных'
                            }
                          </div>
                        </div>
                        
                        <div className="info-item">
                          <div className="info-label">Время работы двигателя</div>
                          <div className="info-value">
                            {statistics.summary.engineWorkTimeMinutes ? 
                              `${formatTime(statistics.summary.engineWorkTimeMinutes)}` : 
                              'Нет данных'
                            }
                          </div>
                        </div>
                        
                        <div className="info-item">
                          <div className="info-label">Среднее напряжение</div>
                          <div className="info-value">
                            {statistics.summary.averageVoltage ? 
                              `${statistics.summary.averageVoltage.toFixed(2)} В` : 
                              'Нет данных'
                            }
                          </div>
                        </div>
                        
                        <div className="info-item">
                          <div className="info-label">Пороговое значение</div>
                          <div className="info-value">
                            {statistics.summary.voltageThreshold ? 
                              `${statistics.summary.voltageThreshold.toFixed(1)} В` : 
                              'Нет данных'
                            }
                          </div>
                        </div>
                        
                        {/* Добавляем информацию о текущем напряжении из последней точки */}
                        {statistics.data && statistics.data.length > 0 && statistics.data[statistics.data.length - 1].voltage && (
                          <div className="info-item">
                            <div className="info-label">Текущее напряжение</div>
                            <div className="info-value">
                              {statistics.data[statistics.data.length - 1].voltage.toFixed(2)} В
                            </div>
                          </div>
                        )}
                        
                        {/* Добавляем информацию о состоянии двигателя из последней точки */}
                        {statistics.data && statistics.data.length > 0 && statistics.data[statistics.data.length - 1].engineState !== undefined && (
                          <div className="info-item">
                            <div className="info-label">Состояние двигателя</div>
                            <div className="info-value">
                              {statistics.data[statistics.data.length - 1].engineState > 0 ? 
                                '🟢 Включен' : 
                                '🔴 Выключен'
                              }
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Блок экологической информации */}
                {statistics && statistics.summary && (
                  <div className="eco-info-block">
                    <div className="info-block-header">
                      <h3>🌱 Экологическая информация</h3>
                    </div>
                    <div className="info-block-content">
                      <div className="info-grid">
                        <div className="info-item">
                          <div className="info-label">Выбросы CO2</div>
                          <div className="info-value">
                            {statistics.summary.co2EmissionVolume ? 
                              `${statistics.summary.co2EmissionVolume.toFixed(2)} кг` : 
                              'Нет данных'
                            }
                          </div>
                        </div>
                        
                        <div className="info-item">
                          <div className="info-label">CO2 на 100км</div>
                          <div className="info-value">
                            {statistics.summary.co2EmissionVolume && statistics.summary.totalDistance ? 
                              `${((statistics.summary.co2EmissionVolume / statistics.summary.totalDistance) * 100).toFixed(2)} кг/100км` : 
                              'Нет данных'
                            }
                          </div>
                        </div>
                        
                        <div className="info-item">
                          <div className="info-label">Эффективность топлива</div>
                          <div className="info-value">
                            {statistics.summary.totalDistance && statistics.summary.fuelConsumption ? 
                              `${(statistics.summary.totalDistance / statistics.summary.fuelConsumption).toFixed(2)} км/л` : 
                              'Нет данных'
                            }
                          </div>
                        </div>
                        
                        <div className="info-item">
                          <div className="info-label">Экономия топлива</div>
                          <div className="info-value">
                            {statistics.summary.fuelConsumptionPer100Km ? 
                              (statistics.summary.fuelConsumptionPer100Km < 10 ? '🟢 Отличная' :
                               statistics.summary.fuelConsumptionPer100Km < 15 ? '🟡 Хорошая' :
                               statistics.summary.fuelConsumptionPer100Km < 25 ? '🟠 Средняя' : '🔴 Высокий расход') : 
                              'Нет данных'
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {statistics && statistics.data && statistics.data.length > 0 && (
                  <div className="statistics-details">
                    <div className="sections-header" onClick={toggleDetailsCollapse}>
                      <h3>Детальная информация</h3>
                      <button className="collapse-button" title={collapsedDetails ? "Развернуть" : "Свернуть"}>
                        <FontAwesomeIcon icon={collapsedDetails ? faExpand : faCompress} />
                      </button>
                    </div>
                    {!collapsedDetails && (
                      <>
                        <div className="details-info">
                          <p>Всего записей: {statistics.totalRecords}</p>
                          <p>Возвращено записей: {statistics.returnedRecords}</p>
                        </div>
                        
                        <div className="data-table-container">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Время</th>
                                <th>Скорость</th>
                                <th>Расстояние</th>
                                <th>Пробег</th>
                                <th>Общий пробег</th>
                              </tr>
                            </thead>
                            <tbody>
                              {statistics.data.slice(0, 10).map((item, index) => (
                                <tr key={index}>
                                  <td>{item.time}</td>
                                  <td>{formatSpeed(item.speed)}</td>
                                  <td>{formatDistance(item.distance)}</td>
                                  <td>{formatDistance(item.mileage)}</td>
                                  <td>{formatDistance(item.totalMileage)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {statistics.data.length > 10 && (
                            <p className="table-note">
                              Показаны первые 10 записей из {statistics.data.length}
                            </p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Отображение когда данных нет, но запрос выполнен успешно */}
                {!loading && !error && statistics && (!statistics.data || statistics.data.length === 0) && (
                  <div className="no-data">
                    <div className="no-data-icon">📊</div>
                    <h3>Нет данных за выбранный период</h3>
                    <p>{statistics.message || 'За указанный период времени не найдено данных о движении транспортного средства.'}</p>
                    <div className="no-data-suggestions">
                      <p><strong>Возможные причины:</strong></p>
                      <ul>
                        <li>Транспортное средство не передавало данные в этот период</li>
                        <li>Выбранный период времени слишком короткий</li>
                        <li>Устройство было отключено или неисправно</li>
                      </ul>
                      <p><strong>Рекомендации:</strong></p>
                      <ul>
                        <li>Попробуйте выбрать более широкий диапазон дат</li>
                        <li>Проверьте состояние устройства</li>
                        <li>Обратитесь к администратору системы</li>
                      </ul>
                    </div>
                    <button onClick={fetchStatistics} className="load-button">
                      <FontAwesomeIcon icon={faSyncAlt} style={{ marginRight: '8px' }} />
                      Повторить запрос
                    </button>
                  </div>
                )}

                {/* Отображение когда данные еще не загружались */}
                {!loading && !error && !statistics && vehicle && dateRange.startDate && dateRange.endDate && (
                  <div className="no-data">
                    <p>Нет данных для отображения</p>
                    <button onClick={fetchStatistics} className="load-button">
                      Загрузить статистику
                    </button>
                  </div>
                )}
              </>
            )}

            {collapsedAll && (
              <div className="collapsed-message">
                <div className="collapsed-icon">📊</div>
                <p>Статистика свернута</p>
                <button onClick={toggleAllCollapse} className="expands-button">
                  <FontAwesomeIcon icon={faExpand} style={{ marginRight: '8px' }} />
                  Развернуть статистику
                </button>
              </div>
            )}
          </div>
        </div>
      </SplitScreenContainer>

      {/* Модальное окно выбора отчета - выносим за пределы SplitScreenContainer */}
      {showReportChooser && (
        <ReportChooser
          onSelectReport={handleReportSelect}
          onClose={handleReportChooserClose}
          selectedVehicle={vehicle}
          originalReport="statistics"
          containerId={containerToFill}
        />
      )}
    </>
  );
};

export default StatisticsReport; 